import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Pagination } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { IconUpload } from '@tabler/icons-react';
import Spinner from '../ogComponent/spinner';

export default function SalarySlipList() {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [salaryDate, setSalaryDate] = useState('');
    const [payMonth, setPayMonth] = useState('');
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const pageSize = 20;
    const navigate = useNavigate();

    // Access permissions array from Redux store
    const permissions = useSelector((state) => state.emp.permissions);
    const token = localStorage.getItem("accessToken");
    const userId = localStorage.getItem("userId");

    const pagePermissions = ["uploadsalaryslipID", "showsalaryslipID"];

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        const filtered = users.filter(user =>
            user.name.toLowerCase().includes(query.toLowerCase())
        );
        setFilteredUsers(filtered);
        setCurrentPage(1);
    }, [query, users]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${process.env.REACT_APP_API}/api/account/users-list/?name=${query}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setUsers(response.data);
            setFilteredUsers(response.data);
        } catch (error) {
            toast.error('Error fetching users');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleCheckboxChange = (userId) => {
        setSelectedUsers(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const handleGenerateSlip = () => {
        if (selectedUsers.length === 0) {
            toast.error('Please select at least one employee');
            return;
        }
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            const payload = {
                salary_given_date: salaryDate,
                pay_month: payMonth,
                employee_ids: selectedUsers,
            };

            await axios.post(
                `${process.env.REACT_APP_API}/api/attendance/salary-slip-generate/`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            toast.success('Salary Added successfully');
            setShowModal(false);
            setSelectedUsers([]);
            setSalaryDate('');
            setPayMonth('');
        } catch (error) {
            toast.error('Error Adding salary!');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const totalPages = Math.ceil(filteredUsers.length / pageSize);
    const paginatedUsers = filteredUsers.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    const handlePageChange = (event, page) => {
        setCurrentPage(page);
    };

    const handleRowClick = (userId) => {
        if (permissions.includes("showsalaryslipID") || String(userId) === String(userId)) {
            navigate(`/user/salaryslip/${userId}`);
        }
    };

    return (
        <div className="px-4 py-3 w-100">
            <h4>Salary Slip List</h4>

            <div className="mb-3 w-100 d-flex justify-content-between align-items-center">
                <input
                    type="text"
                    className="form-control w-50"
                    placeholder="Search by name..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />
                {permissions.includes("uploadsalaryslipID") && (
                    <button
                        className="btn btn-success mt-3"
                        onClick={handleGenerateSlip}
                        disabled={loading}
                    >
                        Upload Salary <IconUpload className='ms-2' stroke={2} size={20} />
                    </button>
                )}
            </div>

            {loading ? (
                <Spinner />
            ) : (
                <>
                    <table className="table newtable table-bordered table-hover">
                        <thead>
                            <tr>
                                {permissions.includes("uploadsalaryslipID") && <th></th>}
                                <th>Sl No</th>
                                <th>User Code</th>
                                <th>Name</th>
                                <th>Job Title</th>
                                <th>Phone</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedUsers.map((user, index) => (
                                <tr
                                    key={user.id}
                                    style={{ cursor: (permissions.includes("showsalaryslipID") || String(user.id) === String(userId)) ? 'pointer' : 'default' }}
                                    onClick={() => handleRowClick(user.id)}
                                >
                                    {permissions.includes("uploadsalaryslipID") && (
                                        <td
                                            onClick={(e) => e.stopPropagation()}
                                            className='d-flex justify-content-center align-items-center'
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedUsers.includes(user.id)}
                                                onChange={(e) => {
                                                    e.stopPropagation();
                                                    handleCheckboxChange(user.id);
                                                }}
                                                style={{ width: "18px", height: "18px" }}
                                            />
                                        </td>
                                    )}
                                    <td>{(currentPage - 1) * pageSize + index + 1}</td>
                                    <td>{user?.employee_code || "N/A"}</td>
                                    <td className="d-flex gap-3 align-items-center px-0">
                                        <img
                                            style={{
                                                width: "25px",
                                                height: "25px",
                                                flexShrink: 0,
                                                borderRadius: "50%",
                                            }}
                                            src={user.profile_photo || "/assets/user.png"}
                                            alt="user img"
                                        />
                                        <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {user.name || "N/A"}
                                        </div>
                                    </td>
                                    <td>{user.job_position?.job_title || 'N/A'}</td>
                                    <td>{user.phone_number || 'N/A'}</td>
                                    <td className={user?.status ? 'text-success' : 'text-danger'}>
                                        {user?.status ? 'Active' : 'In-Active' || 'N/A'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className='w-100 d-flex justify-content-center mb-4'>
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={handlePageChange}
                            color="primary"
                            size="small"
                            className="mt-1 mt-sm-0"
                            disabled={loading}
                        />
                    </div>
                </>
            )}

            {permissions.includes("uploadsalaryslipID") && (
                <div
                    className={`modal fade ${showModal ? 'show' : ''}`}
                    style={{ display: showModal ? 'block' : 'none' }}
                    tabIndex="-1"
                >
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Generate Salary Slip</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <form onSubmit={handleSubmit}>
                                    <div className="mb-3">
                                        <label className="form-label">Salary Given Date</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={salaryDate}
                                            onChange={(e) => setSalaryDate(e.target.value)}
                                            required
                                            disabled={loading}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Pay Month</label>
                                        <input
                                            type="month"
                                            className="form-control"
                                            value={payMonth}
                                            onChange={(e) => setPayMonth(e.target.value)}
                                            required
                                            disabled={loading}
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={loading}
                                    >
                                        {loading ? 'Submitting...' : 'Submit'}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {permissions.includes("uploadsalaryslipID") && showModal && <div className="modal-backdrop fade show"></div>}

            <ToastContainer position="bottom-right" autoClose={3000} />
        </div>
    );
}